// Allowed: Latin letters (A-Z plus Nordic/extended: ÅÄÖÆØÜ), digits, dash, space
const DISALLOWED = /[^A-ZÅÄÖÆØÜÀÁÂÃÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÙÚÛÝÞŸ0-9\- ]/g;
const MIN_LENGTH = 2;
const MAX_LENGTH = 9;

function sanitize(value) {
    return value.toUpperCase().replace(DISALLOWED, "");
}

function isValid(value) {
    return value.length >= MIN_LENGTH && value.length <= MAX_LENGTH;
}

function getErrorSpan(input) {
    return input.closest(".Form__Element")?.querySelector("[data-license-plate-error]");
}

function hideEpiError(input) {
    const span = input.closest(".Form__Element")?.querySelector("[data-f-validationerror]");
    if (span) span.style.display = "none";
}

function restoreEpiError(input) {
    const span = input.closest(".Form__Element")?.querySelector("[data-f-validationerror]");
    if (span) span.style.removeProperty("display");
}

function showError(input, message) {
    hideEpiError(input);
    const span = getErrorSpan(input);
    if (span) {
        if (!span.dataset.defaultMessage) {
            span.dataset.defaultMessage = span.textContent.trim();
        }
        span.textContent = message ?? span.dataset.defaultMessage;
        span.style.removeProperty("display");
    }
    input.setAttribute("aria-invalid", "true");
}

function clearError(input) {
    const span = getErrorSpan(input);
    if (span) {
        if (span.dataset.defaultMessage) span.textContent = span.dataset.defaultMessage;
        span.style.display = "none";
    }
    input.removeAttribute("aria-invalid");
    restoreEpiError(input);
}

function applyFormatting(input) {
    const pos = input.selectionStart;
    const sanitized = sanitize(input.value);
    if (sanitized !== input.value) {
        input.value = sanitized;
        // Restore cursor; clamp in case characters before cursor were removed
        try {
            input.setSelectionRange(pos, pos);
        } catch (_) {}
    }
}

function updateErrorState(input) {
    const val = input.value;
    if (val.length === 0) {
        clearError(input);
        return;
    }
    if (val.length < MIN_LENGTH) {
        showError(input, input.dataset.minlengthError || "The license plate should have more than 2 chars");
    } else if (val.length > MAX_LENGTH) {
        showError(input);
    } else {
        clearError(input);
    }
}

function handleInput(e) {
    hideEpiError(e.target);
    applyFormatting(e.target);
    const len = e.target.value.length;
    if (len > MAX_LENGTH) {
        showError(e.target);
    } else if (len < MAX_LENGTH && (len === 0 || isValid(e.target.value))) {
        // Do not clear at exactly MAX_LENGTH — the keydown handler may have just shown
        // the max-length error and some browsers fire a redundant input event even when
        // the character was blocked by maxlength.
        clearError(e.target);
    }
}

function handleChange(e) {
    applyFormatting(e.target);
    updateErrorState(e.target);
}

function handleKeyDown(e) {
    const input = e.target;
    if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;
    const selectionLength = Math.abs((input.selectionEnd || 0) - (input.selectionStart || 0));
    if (input.value.length - selectionLength + 1 > MAX_LENGTH) {
        showError(input, input.dataset.maxlengthError || "The license plate cannot be longer than 9 characters");
        setTimeout(() => { if (isValid(input.value)) clearError(input); }, 2000);
    }
}

function handlePaste(e) {
    // Let the paste land, then sanitize and re-evaluate on next tick
    setTimeout(() => {
        applyFormatting(e.target);
        updateErrorState(e.target);
    }, 0);
}

// Validate all license plate fields in a container; returns true if all valid
function validateContainer(container) {
    let allValid = true;
    container.querySelectorAll("input[data-license-plate]").forEach((input) => {
        // Skip inputs inside hidden steps
        if (!input.offsetParent) return;
        if (input.value.length === 0) return; // RequiredValidator handles empty
        if (input.value.length < MIN_LENGTH) {
            showError(input, input.dataset.minlengthError || "The license plate should have more than 2 chars");
            allValid = false;
        } else if (!isValid(input.value)) {
            showError(input);
            allValid = false;
        } else {
            clearError(input);
        }
    });
    return allValid;
}

export { sanitize, isValid, showError, clearError, updateErrorState, applyFormatting, validateContainer, handleInput, handleChange, handleKeyDown };

// Intercept Next/Submit button clicks before EPiServer Forms processes them
document.addEventListener(
    "click",
    function (e) {
        const btn = e.target.closest(
            '.btnNext, button[type="submit"]:not(.btnPrev)',
        );
        if (!btn) return;

        const form = btn.closest(".EPiServerForms");
        if (!form) return;

        const currentStep = form.querySelector(".FormStep:not(.hide)") || form;
        if (!validateContainer(currentStep)) {
            e.preventDefault();
            e.stopImmediatePropagation();
            const firstInvalid = currentStep.querySelector(
                "input[data-license-plate][aria-invalid]",
            );
            if (firstInvalid) {
                firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
                firstInvalid.focus();
            }
        }
    },
    true, // capture phase — runs before EPiServer Forms bubble-phase handlers
);

document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll("input[data-license-plate]").forEach((input) => {
        input.addEventListener("keydown", handleKeyDown);
        input.addEventListener("input", handleInput);
        input.addEventListener("change", handleChange);
        input.addEventListener("paste", handlePaste);
    });
});
